import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import { API_BASE_URL } from 'src/apiConfig';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

/* ADMIN KOMPONENTA ZA FINALNE REŠENJA - EDITOR (CHECBOX + POENI) */

const parseSolutionString = (solutionString = '') => {
  const regex = /(\d+)([A-Z])/g;
  const parsedTasks = {};
  let match;
  while ((match = regex.exec(solutionString)) !== null) {
    parsedTasks[parseInt(match[1])] = match[2];
  }
  return parsedTasks;
};

const parsePointsString = (pointsString = '') => {
  return pointsString.split(':').slice(1, -1).map(Number).filter(Boolean);
};

const getTaskCountByGrade = (gradeString) => {
  const g = parseInt(gradeString);
  if (g === 1 || g === 2) return 12;
  if (g === 3 || g === 4) return 15;
  return 18;
};

function FinalGradeSolutionEditor({
  grade,
  solutionString,
  pointsString,
  onSave,
  onCancel
}) {
  const { t } = useTranslation();
  const [parsedTasks, setParsedTasks] = useState(
    parseSolutionString(solutionString)
  );
  const [parsedPoints, setParsedPoints] = useState(
    parsePointsString(pointsString)
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCheckboxChange = (taskNumber, letter) => {
    setParsedTasks((prev) => ({
      ...prev,
      [taskNumber]: prev[taskNumber] === letter ? '' : letter
    }));
  };

  const handlePointsChange = (index, value) => {
    const newPoints = [...parsedPoints];
    newPoints[index] = value;
    setParsedPoints(newPoints);
  };

  const handleSave = async () => {
    try {
      const solutionsString = Object.entries(parsedTasks)
        .map(([task, letter]) => `${task}${letter}`)
        .join(':');

      const pointsString =
        parsedPoints.map((point) => `:${point}`).join('') + ':';

      const response = await axios.put(
        `${API_BASE_URL}/api/finalGradeSolutions.php?grade=${grade}`,
        {
          solutions: solutionsString,
          points: pointsString
        }
      );

      await axios.get(
        `${API_BASE_URL}/api/finalReviewAll.php?action=recalculatePoints`
      );

      if (response.status === 200) {
        setSnackbarOpen(true);
        onSave();
      }
    } catch (error) {
      console.error('Error updating solution', error);
    }
  };

  return (
    <Box
      sx={{
        padding: 2,
        borderRadius: '10px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#f5f5f5',
        maxWidth: '300px',
        margin: 'auto',
        marginTop: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Table
        sx={{
          minWidth: 300,
          '& .MuiTableCell-root': {
            padding: '2px 4px',
            textAlign: 'center',
            fontSize: '10px',
            lineHeight: '1'
          },
          '& .MuiCheckbox-root': {
            padding: '0px',
            color: '#1c75bc',
            '&.Mui-checked': {
              color: '#1c75bc'
            }
          }
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('solutions.taskNumber')}</TableCell>
            {['A', 'B', 'V', 'G', 'D', 'X'].map((letter) => (
              <TableCell key={letter}>
                {t(`solutions.letters.${letter}`)}
              </TableCell>
            ))}
            <TableCell>{t('solutions.points')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: getTaskCountByGrade(grade) }, (_, i) => i + 1).map((taskNumber) => (
            <TableRow key={taskNumber}>
              <TableCell>{taskNumber}.</TableCell>
              {['A', 'B', 'V', 'G', 'D', 'X'].map((letter) => (
                <TableCell key={letter}>
                  <Checkbox
                    size="small"
                    checked={parsedTasks[taskNumber] === letter}
                    onChange={() => handleCheckboxChange(taskNumber, letter)}
                  />
                </TableCell>
              ))}
              <TableCell>
                <TextField
                  value={parsedPoints[taskNumber - 1] || ''}
                  size="small"
                  variant="outlined"
                  inputProps={{
                    style: { fontSize: '10px', padding: '2px' }
                  }}
                  sx={{ width: '40px' }}
                  onChange={(e) =>
                    handlePointsChange(taskNumber - 1, e.target.value)
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 2,
          width: '100%'
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{
            fontSize: '10px',
            padding: '4px 8px',
            backgroundColor: '#2596be',
            '&:hover': {
              backgroundColor: '#1976d2'
            },
            flexGrow: 1
          }}
        >
          {t('solutions.save')}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            fontSize: '10px',
            padding: '4px 8px',
            borderColor: '#2596be',
            color: '#2596be',
            '&:hover': {
              backgroundColor: '#f0f0f0'
            },
            flexGrow: 1
          }}
        >
          {t('solutions.cancel')}
        </Button>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {t('solutions.updatedSuccessfully')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FinalGradeSolutionEditor;
